import { flatMap, get, set, unset } from "lodash";
import moment from "moment";
import tinycolor from "tinycolor2";

import {
  containsBindingsAnywhere,
  Dimension,
  WidgetTypes,
} from "@superblocksteam/shared";
import type {
  Padding,
  PerCornerBorderRadius,
  PerSideBorder,
  Typographies,
} from "@superblocksteam/shared";

import { AI_EDITABLE_WIDGET_TYPES } from "ai/AiComponentEditor/constants";
import {
  NEUTRAL_COLOR_KEYS,
  THEME_COLOR_KEYS,
} from "legacy/components/propertyControls/ColorPickerControl";
import { getOutputComputedValue } from "legacy/components/propertyControls/ComputeTablePropertyControl";
import type {
  PropertyPaneConfig,
  PropertyPaneControlConfig,
} from "legacy/constants/PropertyControlConstants";
import { CanvasLayout, Position } from "legacy/constants/WidgetConstants";
import { ISO_DATE_FORMAT } from "legacy/constants/WidgetValidation";
import {
  getWidgetPropertyThemeValue,
  isPropertyRemovableOrResettable,
} from "legacy/pages/Editor/PropertyPane/widgetPropertyPaneConfigUtils";
import type { GeneratedTheme } from "legacy/themes";
import { getAvailableFontFamilies } from "legacy/themes/typefaces/utils";
import {
  DEFAULT_INPUT_TEXT_STYLE_VARIANT,
  SB_CUSTOM_TEXT_STYLE,
} from "legacy/themes/typographyConstants";
import { isSubProperty } from "legacy/utils/ai";
import type { DynamicPath } from "legacy/utils/DynamicBindingTypes";
import { unsetPropertyPath } from "legacy/utils/DynamicBindingUtils";
import type { WidgetProps } from "legacy/widgets/BaseWidget/types";
import { DEFAULT_BUTTON_WIDGET_TEXT_STYLE_VARIANT } from "legacy/widgets/ButtonWidget/constants";
import { normalizeDateFormat } from "legacy/widgets/DatePickerWidget/utils";
import {
  DEFAULT_INPUT_WIDGET_INPUT_STYLE_VARIANT,
  DEFAULT_INPUT_WIDGET_LABEL_STYLE_VARIANT,
} from "legacy/widgets/InputWidget/InputWidgetConstants";
import { customStylesProperties } from "legacy/widgets/styleProperties";
import {
  DEFAULT_CELL_TEXT_STYLE_VARIANT,
  DEFAULT_HEADING_TEXT_STYLE_VARIANT,
  TABLE_COLUMN_HEADER_DEFAULT_TYPOGRAPHY,
} from "legacy/widgets/TableWidget/TableComponent/Constants";

import type { AllFlags } from "../featureFlags";
import { getColumnIdFromAi } from "./columnUtils";
import { ClarkFocusedEntityType } from "./types";
import type { DiscardedEdit } from "./types";

const VALID_DIMENSIONS_BY_PARENT_LAYOUT = {
  width: {
    [CanvasLayout.FIXED]: ["gridUnit"],
    [CanvasLayout.HSTACK]: ["gridUnit", "px", "fitContent", "fillParent"],
    [CanvasLayout.VSTACK]: ["gridUnit", "px", "fitContent", "fillParent"],
  },
  height: {
    [CanvasLayout.FIXED]: ["gridUnit", "fitContent"],
    [CanvasLayout.HSTACK]: ["gridUnit", "fitContent", "fillParent"],
    [CanvasLayout.VSTACK]: ["gridUnit", "fitContent", "fillParent"],
  },
};

type BaseProps = {
  changesByWidgetId: Record<string, Record<string, any> | null>;
  discardedEdits: Record<string, DiscardedEdit[]>;
  dynamicProperties: DynamicPath[];
  previousWidget: Partial<WidgetProps>;
  theme: GeneratedTheme;
  featureFlags: Partial<AllFlags>;
  changedKeys: string[];
  widgets: Record<string, Partial<WidgetProps>>;
};

const parseDimension = (
  value: string | number | object,
): undefined | Dimension<"px"> => {
  if (typeof value === "string") {
    if (value.endsWith("px")) {
      const val = parseInt(value.replace("px", ""));
      if (isNaN(val)) {
        return undefined;
      }
      return Dimension.px(val);
    } else {
      // try to just parse the number
      const val = parseInt(value);
      if (isNaN(val)) {
        return undefined;
      }
      return Dimension.px(val);
    }
  }
  if (typeof value === "number") {
    return Dimension.px(value);
  }
  if (
    typeof value === "object" &&
    value != null &&
    "value" in value &&
    (typeof value.value === "number" || typeof value.value === "string")
  ) {
    let numValue = value.value;
    if (typeof numValue === "string") {
      numValue = parseInt(numValue);
    }
    return Dimension.px(numValue);
  }
  if (typeof value === "object" && value != null && "width" in value) {
    return parseDimension(value.width as any);
  }
};

const UNEDITABLE_PROPERTY_NAMES = new Set(["widgetId"]);

const sanitizeEdit = (
  props: BaseProps & {
    propertyName: string;
    config: PropertyPaneControlConfig;
    edits: Record<string, any>;
  },
) => {
  const {
    propertyName,
    config,
    edits,
    changesByWidgetId,
    discardedEdits,
    dynamicProperties,
    previousWidget,
    theme,
    featureFlags,
    widgets,
  } = props;

  const addDynamicProperty = (key: string) => {
    dynamicProperties.push({ key });
  };
  const removeDynamicProperty = (key: string) => {
    const indexToRemove = dynamicProperties.findIndex(
      (prop) => prop.key === key,
    );
    if (indexToRemove !== -1) {
      dynamicProperties.splice(indexToRemove, 1);
    }
  };

  const copyDerivedColumnChange = (propertyName: string, value: string) => {
    const columnName = propertyName.split(".")[1];
    if (
      columnName &&
      ((previousWidget as any).derivedColumns?.[columnName] ||
        !(previousWidget as any).primaryColumns?.[columnName])
    ) {
      const restOfPath = propertyName.split(".").slice(1).join(".");
      const derivedProperty = `derivedColumns.${restOfPath}`;
      set(edits, derivedProperty, value);
    }
  };

  if (UNEDITABLE_PROPERTY_NAMES.has(propertyName)) {
    const widgetId = previousWidget.widgetId ?? "";
    discardedEdits[widgetId].push({
      propertyName,
      reason: `Cannot update ${propertyName}`,
      propertyValue: get(edits, propertyName),
    });
    unset(edits, propertyName);
    removeDynamicProperty(propertyName);
    return;
  }

  const value = get(edits, propertyName);
  // special case for text style properties of columns. These won't use the text_Style control, but should be handled the same way
  if (
    propertyName.startsWith("primaryColumns") &&
    propertyName.includes("cellProps.textStyle")
  ) {
    // todo: probably should sanitize this
    if (
      !propertyName.includes("variant") &&
      !propertyName.includes("textColor")
    ) {
      const parentPropertyName = propertyName.split(".").slice(0, -1).join(".");
      set(edits, `${parentPropertyName}.variant`, SB_CUSTOM_TEXT_STYLE);
    }
  }

  const { isRemovable, isResettable } = isPropertyRemovableOrResettable(
    config,
    previousWidget,
    { theme, featureFlags },
  );
  if (value === undefined && (isRemovable || isResettable)) {
    // the property has been removed or reset, there's nothing to verify
    return;
  }

  // based on the control type, we need to validate in different ways
  switch (config.controlType) {
    case "STICKY_POSITION": {
      if (!Object.values(Position).includes(value)) {
        // if the value is not a valid position, set it to undefined, which is the default
        set(edits, propertyName, undefined);
      }
      break;
    }
    case "LOCATION_SEARCH": {
      // make it dynamic
      addDynamicProperty(propertyName);
      break;
    }
    case "DATE_PICKER": {
      if (typeof value !== "string") {
        const widgetId = previousWidget.widgetId ?? "";
        discardedEdits[widgetId].push({
          propertyName,
          reason: "Invalid date value",
          propertyValue: value,
        });
      }
      const dateFormat =
        get(edits, "dateFormat") || get(previousWidget, "dateFormat");
      const normalizedFormat = normalizeDateFormat(
        dateFormat || ISO_DATE_FORMAT,
      );
      if (!moment(value, normalizedFormat, true).isValid()) {
        addDynamicProperty(propertyName);
      }
      break;
    }
    case "COLOR_PICKER": {
      const {
        isValid,
        isDynamic,
        value: parsedValue,
      } = colorPickerContainsColor(value);
      set(edits, propertyName, parsedValue);
      if (!isValid) {
        const widgetId = previousWidget.widgetId ?? "";
        discardedEdits[widgetId].push({
          propertyName,
          reason: "Invalid color value",
          propertyValue: value,
        });
      } else if (!isDynamic) {
        removeDynamicProperty(propertyName);
      } else {
        addDynamicProperty(propertyName);
      }
      break;
    }
    case "TEXT_STYLE": {
      if (typeof value !== "object" || value === null) {
        const widgetId = previousWidget.widgetId ?? "";
        discardedEdits[widgetId].push({
          propertyName,
          reason: "Invalid text style value",
          propertyValue: value,
        });
        unset(edits, propertyName);
        break;
      }
      sanitizeTextStyle({
        ...props,
        edits,
        value,
        propertyName,
        addDynamicProperty,
        setTopLevelProperty: (path: string, value: any) => {
          if (value === undefined) {
            unset(edits, path);
          } else {
            set(edits, path, value);
          }
        },
      });
      break;
    }
    case "BORDER_CONTROL": {
      if (typeof value !== "object" || value === null) {
        const widgetId = previousWidget.widgetId ?? "";
        discardedEdits[widgetId].push({
          propertyName,
          reason: "Invalid border value",
          propertyValue: value,
        });
        unset(edits, propertyName);
        break;
      }
      const parsedBorder: any = {};
      let borderColor: string | undefined;
      const themeValue = getWidgetPropertyThemeValue({
        props: config,
        propertyName,
        itemProperties: previousWidget,
        flags: featureFlags,
        theme,
        widgets,
      });
      for (const side of [
        "left",
        "right",
        "top",
        "bottom",
      ] as (keyof PerSideBorder)[]) {
        const existingColor: string | undefined =
          get(previousWidget, `${propertyName}.${side}.color`) ??
          themeValue?.[side]?.color;
        const existingWidth =
          get(previousWidget, `${propertyName}.${side}.width`) ??
          themeValue?.[side]?.width;

        const updatedColor =
          get(value, `${side}.color`) !== existingColor
            ? get(value, `${side}.color`)
            : undefined;

        if (updatedColor) {
          const {
            value: color,
            isValid,
            isDynamic,
          } = colorPickerContainsColor(updatedColor);
          if (isValid && !isDynamic) {
            borderColor = color;
          }
        }

        let updatedWidth = get(value, `${side}.width`);
        if (updatedWidth) {
          updatedWidth = parseDimension(updatedWidth);
        }
        parsedBorder[side] = {
          style: "solid",
          width: updatedWidth ?? existingWidth,
          color: updatedColor ?? existingColor,
        };
      }
      // if borderColor is set, set it on all sides
      if (borderColor) {
        for (const side of [
          "left",
          "right",
          "top",
          "bottom",
        ] as (keyof PerSideBorder)[]) {
          parsedBorder[side] = {
            ...parsedBorder[side],
            color: borderColor,
          };
        }
      }
      set(edits, propertyName, parsedBorder);

      break;
    }
    case "PADDING_CONTROL":
    case "MARGIN_CONTROL": {
      if (typeof value !== "object" || value === null || Array.isArray(value)) {
        const widgetId = previousWidget.widgetId ?? "";
        discardedEdits[widgetId].push({
          propertyName,
          reason: "Invalid padding value",
          propertyValue: value,
        });
        unset(edits, propertyName);
        break;
      }
      const themeValue = getWidgetPropertyThemeValue({
        props: config,
        propertyName,
        itemProperties: previousWidget,
        flags: featureFlags,
        theme,
        widgets,
      });
      const targetWidgetId = config.getTargetWidgetId
        ? config.getTargetWidgetId(previousWidget as any)
        : undefined;
      const parsedPadding: Padding = {};
      (["left", "right", "top", "bottom"] as (keyof Padding)[]).forEach(
        (side) => {
          if (!value[side]) {
            parsedPadding[side] = get(
              targetWidgetId
                ? (widgets[targetWidgetId] ?? previousWidget)
                : previousWidget,
              `${propertyName}.${side}`,
              get(themeValue, side),
            ) as Dimension<"px"> | undefined;
          } else {
            parsedPadding[side] = parseDimension(value[side]);
          }
        },
      );
      set(edits, propertyName, parsedPadding);
      break;
    }
    case "BORDER_RADIUS_CONTROL": {
      if (typeof value !== "object" || value === null) {
        const widgetId = previousWidget.widgetId ?? "";
        discardedEdits[widgetId].push({
          propertyName,
          reason: "Invalid border radius value",
          propertyValue: value,
        });
        unset(edits, propertyName);
        break;
      }
      const themeValue = getWidgetPropertyThemeValue({
        props: config,
        propertyName,
        itemProperties: previousWidget,
        flags: featureFlags,
        theme,
        widgets,
      });
      const parsedBorderRadius: Record<string, Dimension<"px">> = {};
      (
        [
          "topLeft",
          "topRight",
          "bottomLeft",
          "bottomRight",
        ] as (keyof PerCornerBorderRadius)[]
      ).forEach((corner: keyof PerCornerBorderRadius) => {
        if (!value[corner]) {
          parsedBorderRadius[corner] = get(
            previousWidget,
            `${propertyName}.${corner}`,
            get(themeValue, corner),
          ) as Dimension<"px">;
        } else {
          parsedBorderRadius[corner] =
            parseDimension(value[corner]) ?? Dimension.px(0);
        }
      });
      set(edits, propertyName, parsedBorderRadius);
      break;
    }
    case "SWITCH": {
      if (value === "true") {
        set(edits, propertyName, true);
        removeDynamicProperty(propertyName);
      } else if (value === "false") {
        set(edits, propertyName, false);
        removeDynamicProperty(propertyName);
      } else if (typeof value !== "boolean") {
        if (config.isJSConvertible) {
          addDynamicProperty(propertyName);
        } else {
          const widgetId = previousWidget.widgetId ?? "";
          discardedEdits[widgetId].push({
            propertyName,
            reason: "Invalid boolean value",
            propertyValue: value,
          });
          unset(edits, propertyName);
        }
      } else {
        removeDynamicProperty(propertyName);
      }
      break;
    }
    case "DROP_DOWN":
    case "RADIO_BUTTON_GROUP":
    case "RADIO_BUTTON": {
      if (config.options && config.options.length) {
        // check if the value is in the options array
        const isInOptions = config.options.some(
          (opt) => opt.value === value || opt === value,
        );
        const isNullOption = config.options.some(
          (opt) => opt.value === undefined,
        );
        if (isInOptions) {
          removeDynamicProperty(propertyName);
        } else if (config.isJSConvertible) {
          addDynamicProperty(propertyName);
        } else if (isNullOption) {
          // use the default option
          set(edits, propertyName, undefined);
        } else {
          // its invalid
          const widgetId = previousWidget.widgetId ?? "";
          discardedEdits[widgetId].push({
            propertyName,
            reason: "Invalid dropdown value",
            propertyValue: value,
          });
          // clear the change
          unset(edits, propertyName);
        }
      }
      // otherwise, it's using options func or selector so just leave as is
      break;
    }
    case "ICON_SELECTOR": {
      if (typeof value === "string") {
        if (value.includes("{{")) {
          addDynamicProperty(propertyName);
        } else {
          removeDynamicProperty(propertyName);
        }
      } else {
        addDynamicProperty(propertyName);
      }
      break;
    }
    case "COMPUTE_TABLE_VALUE": {
      if (typeof value === "string" && !value.includes("tableData")) {
        const computedValue = getOutputComputedValue(
          previousWidget?.widgetName ?? "",
          value,
        );
        set(edits, propertyName, computedValue);
        copyDerivedColumnChange(propertyName, computedValue);
      }
      break;
    }
    case "INPUT_NUMBER": {
      if (typeof value === "number") {
        const unit = config.defaultUnit;
        set(edits, propertyName, `${value}${unit ?? ""}`);
      } else if (typeof value !== "string") {
        const widgetId = previousWidget.widgetId ?? "";
        discardedEdits[widgetId].push({
          propertyName,
          reason: "Invalid value",
          propertyValue: value,
        });
        unset(edits, propertyName);
      } else {
        // make sure that the string includes the unit
        const possibleUnits = config.unitOptions ?? [];
        if (possibleUnits.length > 0) {
          const isValid = possibleUnits.some(
            (unit) => unit.value?.length === 0 || value.endsWith(unit.value),
          );
          if (isValid) {
            set(edits, propertyName, value);
          } else {
            set(edits, propertyName, `${value}${config.defaultUnit ?? ""}`);
          }
        }
      }
      break;
    }
    case "DEFAULT_TABLE_SORT": {
      // transform column from ai value to real column id
      const columnName = value?.column;
      if (columnName) {
        const columnId = getColumnIdFromAi(
          columnName,
          (previousWidget as any).primaryColumns ?? {},
        );
        if (columnId) {
          set(edits, `${propertyName}.column`, columnId);
        }
      }
      break;
    }
    case "CUSTOM_TAG_COLORS": {
      let parsedValue = value;
      if (typeof value === "string") {
        try {
          parsedValue = JSON.parse(value);
        } catch (e) {
          // do nothing, maybe its a binding
        }
      }
      if (typeof parsedValue === "object" && parsedValue != null) {
        // transform into the correct format (if not already)
        const tagColors: Record<string, { color: string }> = {
          ...get(previousWidget, propertyName, {}),
        };
        Object.entries(parsedValue).forEach(([key, color]) => {
          if (typeof color === "string") {
            tagColors[key] = { color };
          } else if (
            typeof color === "object" &&
            color != null &&
            "color" in color
          ) {
            tagColors[key] = color as { color: string };
          }
        });
        set(edits, propertyName, tagColors);
      }
      break;
    }

    case "HEIGHT_DROPDOWN":
    case "WIDTH_DROPDOWN":
      {
        const parentLayout = previousWidget?.parentId
          ? (widgets[previousWidget.parentId]?.layout ?? CanvasLayout.FIXED)
          : CanvasLayout.FIXED;
        const validOptions =
          VALID_DIMENSIONS_BY_PARENT_LAYOUT[
            config.controlType === "HEIGHT_DROPDOWN" ? "height" : "width"
          ][parentLayout];
        if (typeof value !== "object" || !("mode" in value)) {
          // discard
          const widgetId = previousWidget.widgetId ?? "";
          discardedEdits[widgetId].push({
            propertyName,
            reason: "Invalid dimension value",
            propertyValue: value,
          });
          unset(edits, propertyName);
        } else {
          if (!validOptions.includes(value.mode)) {
            const widgetId = previousWidget.widgetId ?? "";
            discardedEdits[widgetId].push({
              propertyName,
              reason: "Invalid dimension value",
              propertyValue: value,
            });
            unset(edits, propertyName);
          } else {
            if (value.mode === "fitContent") {
              set(edits, propertyName, Dimension.fitContent(value.value ?? 4));
            } else if (value.mode === "fillParent") {
              set(edits, propertyName, Dimension.fillParent(value.value ?? 4));
            } else if (value.mode === "px") {
              set(edits, propertyName, Dimension.px(value.value ?? 100));
            } else if (value.mode === "gridUnit") {
              set(edits, propertyName, Dimension.gridUnit(value.value ?? 4));
            }
          }
        }
      }
      break;

    default:
      // special case for custom validation rule, which always needs to be dynamic
      if (config.propertyName === "customValidationRule") {
        if (typeof value === "string" && !value.trim().startsWith("{{")) {
          // wrap value in bindings if needed
          set(edits, propertyName, `{{ ${value} }}`);
        }
      }
      if (
        config.isJSConvertible &&
        edits.dynamicBindingPathList?.some(
          (prop: DynamicPath) => prop.key === propertyName,
        )
      ) {
        addDynamicProperty(propertyName);
      }
      break;
  }

  switch (config.customJSControl) {
    case "COMPUTE_TABLE_VALUE": {
      const value = get(edits, propertyName);
      if (
        typeof value === "string" &&
        containsBindingsAnywhere(value) &&
        !value.includes("tableData")
      ) {
        const computedValue = getOutputComputedValue(
          previousWidget?.widgetName ?? "",
          value,
        );
        set(edits, propertyName, computedValue);
        copyDerivedColumnChange(propertyName, computedValue);
      }
      break;
    }
  }

  // special case for when the property should actually be applied on a different widget (i.e. on the canvas child of container)
  if (config.getTargetWidgetId) {
    const targetWidgetId = config?.getTargetWidgetId(
      previousWidget as any as WidgetProps,
    );
    if (targetWidgetId) {
      const value = get(edits, propertyName);
      unset(edits, propertyName);
      set(changesByWidgetId, `${targetWidgetId}.${propertyName}`, value);
      // TODO: do we need to update changedKeys?
    }
  }
};

const colorPickerContainsColor = (
  value: unknown,
): {
  isValid: boolean;
  isDynamic: boolean;
  value: string | undefined;
} => {
  let valToTest = value;
  if (typeof value === "object" && value !== null) {
    const colorValue = Object.values(value)[0];
    if (typeof colorValue === "string") {
      valToTest = colorValue;
    } else {
      return {
        isValid: false,
        value: undefined,
        isDynamic: false,
      };
    }
  }
  if (typeof valToTest !== "string") {
    return {
      isValid: false,
      value: undefined,
      isDynamic: false,
    };
  }
  const builtIns = [...THEME_COLOR_KEYS, ...NEUTRAL_COLOR_KEYS];
  for (let i = 0; i < builtIns.length; i++) {
    const colorKey = builtIns[i];
    if (valToTest === `{{ theme.colors.${colorKey} }}`) {
      return {
        isValid: true,
        value: valToTest,
        isDynamic: false,
      };
    } else if (valToTest === colorKey) {
      return {
        isValid: true,
        value: `{{ theme.colors.${colorKey} }}`,
        isDynamic: false,
      };
    }
  }
  if (/^#[0-9A-F]{6}[0-9a-f]{0,2}$/i.test(valToTest)) {
    return {
      isValid: true,
      value: valToTest,
      isDynamic: false,
    };
  }
  // try converting to a hex color
  const hexColor = tinycolor(valToTest);
  if (hexColor.isValid()) {
    return {
      isValid: true,
      value:
        hexColor.getAlpha() === 1
          ? hexColor.toHexString()
          : hexColor.toHex8String(),
      isDynamic: false,
    };
  }

  return {
    isValid: true,
    value: valToTest,
    isDynamic: true,
  };
};

// TODO: add all of the widget types and use this in the property pane config
const getDefaultVariantForWidgetType = (
  widgetType: WidgetTypes,
  pathName: string,
): keyof Typographies => {
  const path = pathName.split(".")[0];
  switch (widgetType) {
    case WidgetTypes.BUTTON_WIDGET:
      if (path === "textProps") {
        return DEFAULT_BUTTON_WIDGET_TEXT_STYLE_VARIANT;
      }
      break;
    case WidgetTypes.INPUT_WIDGET:
      if (path === "labelProps") {
        return DEFAULT_INPUT_WIDGET_LABEL_STYLE_VARIANT;
      } else if (path === "inputProps") {
        return DEFAULT_INPUT_WIDGET_INPUT_STYLE_VARIANT;
      }
      break;
    case WidgetTypes.TABLE_WIDGET:
      if (path === "headerProps") {
        return DEFAULT_HEADING_TEXT_STYLE_VARIANT;
      }
      if (path === "columnHeaderProps") {
        return TABLE_COLUMN_HEADER_DEFAULT_TYPOGRAPHY;
      }
      if (path === "cellProps") {
        return DEFAULT_CELL_TEXT_STYLE_VARIANT;
      }
      if (path === "searchProps") {
        return DEFAULT_INPUT_TEXT_STYLE_VARIANT;
      }
      break;
  }
  return "label";
};

const sanitizeTextStyle = (
  props: BaseProps & {
    value: Record<string, any>;
    propertyName: string;
    setTopLevelProperty: (path: string, value: any) => void;
    addDynamicProperty: (path: string) => void;
    edits: Record<string, any>;
  },
) => {
  const {
    previousWidget,
    theme,
    value,
    propertyName,
    setTopLevelProperty,
    discardedEdits,
    addDynamicProperty,
    edits,
  } = props;

  if (previousWidget.type === WidgetTypes.BUTTON_WIDGET && edits.textColor) {
    const {
      value: color,
      isValid,
      isDynamic,
    } = colorPickerContainsColor(edits.textColor);
    if (isValid) {
      setTopLevelProperty("textColor", color);
      if (isDynamic) {
        addDynamicProperty("textColor");
      }
    } else {
      const widgetId = previousWidget.widgetId ?? "";
      discardedEdits[widgetId].push({
        propertyName: `${propertyName}.textColor`,
        reason: "Invalid text color",
        propertyValue: edits.textColor,
      });
    }
  }

  const prevVariant =
    get(previousWidget, `${propertyName}.variant`) ??
    (getDefaultVariantForWidgetType(
      previousWidget.type as WidgetTypes,
      propertyName,
    ) as string);

  const customStyleProperties = customStylesProperties({
    textStyleParentDottedPath: "",
    forceFullPropertyNamePath: false,
    isPopover: false,
  });

  Object.entries(value).forEach(([nestedPropertyName, nestedValue]) => {
    // if something other than variant or color is set, we need to set variant to custom
    if (
      nestedPropertyName !== "variant" &&
      nestedPropertyName !== "textColor"
    ) {
      value.variant = SB_CUSTOM_TEXT_STYLE;
    }
    // parse the color. for buttons, we need to hoist the color to the top level
    if (nestedPropertyName === "textColor") {
      const {
        value: color,
        isValid,
        isDynamic,
      } = colorPickerContainsColor(nestedValue);
      if (isValid && previousWidget.type !== WidgetTypes.BUTTON_WIDGET) {
        set(value, `textColor.default`, color);
        if (isDynamic) {
          addDynamicProperty(`${propertyName}.textColor.default`);
        }
      }
      return;
    }

    // ensure that the variant is valid
    if (
      nestedPropertyName === "variant" &&
      nestedValue !== SB_CUSTOM_TEXT_STYLE
    ) {
      // check if its valid
      const validTypographies = [
        ...Object.keys(theme.typographies),
        ...Object.keys(theme.typographies.custom ?? {}).map(
          (customKey) => `custom.${customKey}`,
        ),
      ];
      if (!validTypographies.includes(nestedValue as string)) {
        const widgetId = previousWidget.widgetId ?? "";
        discardedEdits[widgetId].push({
          propertyName: `${propertyName}.${nestedPropertyName}`,
          reason: "Invalid typogpraphy",
          propertyValue: nestedValue,
        });
        setTopLevelProperty(propertyName.split(".")[0], undefined);
        return;
      }
    }
    if (nestedPropertyName === "fontFamily") {
      const validFontFamilies = Object.keys(
        getAvailableFontFamilies(theme.availableFonts),
      );
      if (!validFontFamilies.includes(nestedValue)) {
        const widgetId = previousWidget.widgetId ?? "";
        discardedEdits[widgetId].push({
          propertyName: `${propertyName}.${nestedPropertyName}`,
          reason: "Invalid font family",
          propertyValue: nestedValue,
        });
      }
    }
    const propertyConfig = customStyleProperties.find(
      (property) => property.propertyName === nestedPropertyName,
    );
    if (propertyConfig) {
      sanitizeEdit({
        ...props,
        propertyName: nestedPropertyName,
        config: propertyConfig,
        edits: value,
      });
    }
  });

  const newVariant = value.variant;

  // If we switched to a custom variant, fill in all of the text styles based on the theme
  if (
    newVariant === SB_CUSTOM_TEXT_STYLE &&
    prevVariant !== SB_CUSTOM_TEXT_STYLE
  ) {
    const themeValues = get(theme, `typographies.${prevVariant}`);
    Object.entries(themeValues).forEach(([themeKey, themeValue]) => {
      if (!value[themeKey]) {
        value[themeKey] = themeValue;
      }
    });
  }
};

export const sanitizeEdits = (
  props: BaseProps & {
    properties: PropertyPaneConfig[];
    parentPath?: string;
  },
) => {
  const {
    changesByWidgetId,
    changedKeys,
    properties,
    parentPath = "",
    previousWidget,
  } = props;

  if (!previousWidget.widgetId) {
    throw new Error("Widget ID is required");
  }

  const edits = changesByWidgetId[previousWidget.widgetId];
  if (!edits) {
    return;
  }

  for (const property of properties) {
    let propertyName = (property as PropertyPaneControlConfig).propertyName;
    if (typeof propertyName === "string") {
      if (parentPath) {
        propertyName = `${parentPath}.${propertyName}`;
      }
    }
    // case 1: this property was changed by the AI and we have a config for it
    if (
      typeof propertyName === "string" &&
      changedKeys.some((key) => key.startsWith(propertyName as string)) &&
      get(edits, propertyName) !== undefined
    ) {
      sanitizeEdit({
        ...props,
        propertyName,
        config: property as PropertyPaneControlConfig,
        edits,
      });
    }
    // case 2: this property was changed by the user and we don't have a config for it
    else if (
      typeof propertyName === "string" &&
      !propertyName.startsWith("primaryColumns")
    ) {
      const typedPropertyName = propertyName as string;
      // look for any properties that start with the propertyName
      // if they are found, combine them into a single property + sanitize it
      const subProperties = Object.keys(edits).filter((key) =>
        isSubProperty(typedPropertyName, key),
      );
      if (subProperties.length > 0) {
        const keysToRemove: string[] = [];
        const combinedProperty: Record<string, any> = {};
        for (const key of subProperties) {
          const value = get(edits, key);
          const keyWithoutPropertyName = key.slice(
            typedPropertyName.length + 1,
          );
          if (value == null) {
            unsetPropertyPath(combinedProperty, keyWithoutPropertyName);
          } else {
            set(combinedProperty, keyWithoutPropertyName, value);
          }
          keysToRemove.push(key);
        }
        if (keysToRemove.length > 0) {
          // remove the old propertie and pull the combined property to the top level
          keysToRemove.forEach((key) => unset(edits, key));
          set(edits, propertyName, combinedProperty);
          sanitizeEdit({
            ...props,
            propertyName,
            config: property as PropertyPaneControlConfig,
            edits,
          });
        }
      }
    }

    // case 3: this property has children, so we sanitize those
    if (property.children) {
      sanitizeEdits({
        ...props,
        properties: property.children,
        parentPath: typeof propertyName === "string" ? propertyName : undefined,
      });
    }
    // case 4: sanitize primary columns of a table, which is a special case since we need to determine all column ids
    if (propertyName === "primaryColumns") {
      const columnConfigs = flatMap(
        (property as any).panelConfig?.children,
        (section) => (section as any)?.children ?? [],
      );
      const allColumnIds = new Set<string>();
      Object.keys((previousWidget as any)?.primaryColumns ?? {})?.forEach(
        (column) => {
          allColumnIds.add(column);
        },
      );
      Object.keys((edits as any)?.primaryColumns ?? {})?.forEach((column) => {
        allColumnIds.add(column);
      });

      for (const columnId of Array.from(allColumnIds)) {
        sanitizeEdits({
          ...props,
          properties: columnConfigs,
          parentPath: `${propertyName}.${columnId}`,
        });
      }
    }
    // case 5: the property has a popover associated with it, so we sanitize the children of that popover
    else if ((property as PropertyPaneControlConfig).panelConfig != null) {
      if (
        (property as PropertyPaneControlConfig).controlType === "EVENT_TRIGGER"
      )
        continue;

      const { children: panelChildren, panelIdPropertyName } = (
        property as PropertyPaneControlConfig
      ).panelConfig!;
      const subPath =
        get(edits, `${propertyName}.${panelIdPropertyName}`) ??
        get(previousWidget, `${propertyName}.${panelIdPropertyName}`);
      const parentPath = `${propertyName}.${subPath ?? panelIdPropertyName}`;
      sanitizeEdits({
        ...props,
        properties: panelChildren,
        parentPath,
      });
    }
  }
};

export const isClarkSupportedEntityType = ({
  entityType,
  entityProps,
}: {
  entityType?: ClarkFocusedEntityType;
  entityProps?: Record<string, unknown>;
}) => {
  if (!entityType || !entityProps) {
    return false;
  }

  if (entityType === ClarkFocusedEntityType.WIDGET) {
    const widgetType = entityProps.type as WidgetTypes;
    return AI_EDITABLE_WIDGET_TYPES.includes(widgetType);
  }

  return false;
};
